import { useState, useCallback } from 'react'
import type { CSSProperties } from 'react'
import { TopBar } from '../components/ui/TopBar'
import { ScreenWrapper } from '../components/ui/ScreenWrapper'
import { RideCard } from '../components/rides/RideCard'
import { COLORS, FONT, SPACING, RADIUS } from '../components/ui/tokens'
import { useRideSearch } from '../hooks/useRides'
import type { SearchRideParams } from '../types/ride'

export function SearchScreen() {
  const { results, loading, error, search, requestSeat } = useRideSearch()
  const [origin, setOrigin] = useState('')
  const [dest, setDest] = useState('')
  const [date, setDate] = useState('')
  const [seats, setSeats] = useState(1)
  const [toast, setToast] = useState<string | null>(null)

  const handleSearch = useCallback(() => {
    const params: SearchRideParams = {}
    if (origin.trim()) params.originName = origin.trim()
    if (dest.trim()) params.destName = dest.trim()
    if (date) params.date = date
    if (seats > 1) params.seatsNeeded = seats
    void search(params)
  }, [search, origin, dest, date, seats])

  const handleRequest = useCallback(async (rideId: string) => {
    try {
      await requestSeat(rideId, seats)
      setToast('Запрос отправлен')
      setTimeout(() => setToast(null), 3000)
    } catch {
      setToast('Ошибка запроса')
      setTimeout(() => setToast(null), 3000)
    }
  }, [requestSeat, seats])

  const inputStyle: CSSProperties = {
    width: '100%', padding: '11px 13px', backgroundColor: COLORS.bgElevated, border: `1px solid ${COLORS.border}`,
    borderRadius: RADIUS.md, color: COLORS.textPrimary, fontSize: FONT.base, outline: 'none', boxSizing: 'border-box', colorScheme: 'dark',
  }

  return (
    <>
      <TopBar title="Поиск поездок" subtitle={`${results.length} найдено`} />
      <ScreenWrapper>
        <div style={{ padding: SPACING.md }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm, marginBottom: SPACING.md }}>
            <input style={inputStyle} placeholder="Откуда" value={origin} onChange={(e) => setOrigin(e.target.value)} />
            <input style={inputStyle} placeholder="Куда" value={dest} onChange={(e) => setDest(e.target.value)} />
            <div style={{ display: 'flex', gap: SPACING.sm }}>
              <input style={{ ...inputStyle, flex: 1 }} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: COLORS.bgElevated, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: '0 12px' }}>
                <button onClick={() => setSeats((s) => Math.max(1, s - 1))} style={seatBtnStyle}>−</button>
                <span style={{ fontSize: FONT.sm, color: COLORS.textPrimary, minWidth: 20, textAlign: 'center' }}>{seats}👤</span>
                <button onClick={() => setSeats((s) => Math.min(6, s + 1))} style={seatBtnStyle}>+</button>
              </div>
            </div>
            <button onClick={handleSearch} disabled={loading} style={{
              padding: '13px', backgroundColor: loading ? COLORS.bgElevated : COLORS.accent, color: loading ? COLORS.textDisabled : '#fff',
              border: 'none', borderRadius: RADIUS.md, fontSize: FONT.base, fontWeight: 600, cursor: loading ? 'default' : 'pointer',
            }}>{loading ? 'Поиск...' : 'Найти'}</button>
          </div>

          {error && <p style={{ color: COLORS.error, fontSize: FONT.sm, marginBottom: SPACING.sm }}>{error}</p>}

          {results.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: `${SPACING.xl}px 0`, color: COLORS.textSecond, fontSize: FONT.sm }}>
              <div style={{ fontSize: 40, marginBottom: SPACING.sm }}>🔍</div>
              <div>Поездки не найдены</div>
              <div style={{ fontSize: FONT.xs, color: COLORS.textDisabled, marginTop: 4 }}>Попробуйте изменить параметры поиска</div>
            </div>
          ) : (
            results.map((ride) => <RideCard key={ride.id} ride={ride} onRequest={() => void handleRequest(ride.id)} />)
          )}
        </div>
      </ScreenWrapper>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', backgroundColor: COLORS.bgCard,
          border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.full, padding: '10px 20px', color: COLORS.textPrimary,
          fontSize: FONT.sm, zIndex: 800, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>{toast}</div>
      )}
    </>
  )
}

const seatBtnStyle: CSSProperties = { background: 'none', border: 'none', color: COLORS.textPrimary, fontSize: 18, cursor: 'pointer', padding: '8px 4px', lineHeight: 1, WebkitTapHighlightColor: 'transparent' }
